import { ApiData, ApiErrorReasonType, ApiState } from 'scenes/common/types';
import { ApiErrorReason } from './types';

export const API_IS_ERROR_STATES = [
  ApiState.Error,
  ApiState.BusinessError,
  ApiState.PaymentError,
];

export const API_HAS_DATA_STATES = [
  ApiState.Reloading,
  ApiState.Loaded,
  ApiState.QuoteSaved,
];

export const getApiData = <T>(apiData: ApiData<T>) => apiData.data as T;

export const getErrorReason = (apiData: ApiData<unknown>) =>
  apiData.errorReason as ApiErrorReason;

export const getApiErrorMessage = (apiData: ApiData<unknown>) =>
  apiData.errorReason?.message;

export const isApiLimitExceeded = (apiData: ApiData<unknown>) =>
  apiData.errorReason?.type === ApiErrorReasonType.ApiLimitReached;

export const isApiError = (apiData: ApiData<unknown>) =>
  apiData.state === ApiState.Error ||
  apiData.errorReason?.type === ApiErrorReasonType.ApiLimitReached;

export const isApiReferral = (apiData: ApiData<unknown>) =>
  apiData.state === ApiState.BusinessError &&
  apiData.errorReason?.type === ApiErrorReasonType.Referral;

export const isPaymentError = (apiData: ApiData<unknown>) =>
  apiData.state === ApiState.PaymentError;

export const isBusinessError = (apiData: ApiData<unknown>) =>
  apiData.state === ApiState.BusinessError;

export const isBlockingError = (apiData: ApiData<unknown>) =>
  apiData.errorReason?.blockingError;

export const isPromoCodeError = (apiData: ApiData<unknown>) =>
  isBusinessError(apiData) &&
  apiData.errorReason?.type === ApiErrorReasonType.InvalidPromoCode;

export const isIneligiblePromoCodeError = (apiData: ApiData<unknown>) =>
  isBusinessError(apiData) &&
  apiData.errorReason?.type === ApiErrorReasonType.IneligiblePromoCode;

export const isLoadingWithNoData = (apiData: ApiData<unknown>) =>
  apiData.state === ApiState.LoadingWithNoData;

export const isQuoteSaved = (apiData: ApiData<unknown>) =>
  apiData.state === ApiState.QuoteSaved;

export const hasData = (apiData: ApiData<unknown> | undefined) =>
  Boolean(apiData && API_HAS_DATA_STATES.includes(apiData.state));

export const isApiReloading = (apiData: ApiData<unknown> | undefined) =>
  apiData?.state === ApiState.Reloading;

export const NOT_YET_LOADED = {
  state: ApiState.NeverLoaded,
};

export const LOADING = {
  state: ApiState.LoadingWithNoData,
};

export const makeApiError = <T>(
  message: string,
  oldData?: ApiData<T>
): ApiData<T> => ({
  state: ApiState.Error,
  errorReason: { message, type: ApiErrorReasonType.ApiError },
  data: oldData?.data,
});

export const makeBusinessError = (
  reason: ApiErrorReasonType,
  quoteNumber: string,
  blockingError?: boolean,
  stage?: string
): ApiData<never> => ({
  state: ApiState.BusinessError,
  errorReason: {
    type: reason,
    quoteNumber,
    blockingError,
    stage,
  },
});

export const makePromoCodeError = <T>(
  reason: ApiErrorReasonType,
  quoteNumber: string,
  oldData: ApiData<T>
): ApiData<T> => ({
  state: ApiState.BusinessError,
  errorReason: { type: reason, quoteNumber },
  data: oldData.data,
});

export const makePaymentError = <T>(
  message: string,
  oldData?: ApiData<T>
): ApiData<T> => ({
  state: ApiState.PaymentError,
  errorReason: { message, type: ApiErrorReasonType.ApiError },
  data: oldData?.data,
});

export const makeApiSuccess = <T>(data: T): ApiData<T> => ({
  state: ApiState.Loaded,
  data,
});

export const makeApiLoaded = <T>(data: ApiData<T>): ApiData<T> => ({
  ...data,
  state: [ApiState.Error, ApiState.PaymentError, ApiState.QuoteSaved].includes(
    data.state
  )
    ? ApiState.Loaded
    : data.state,
});

export const makeApiReloading = <T>(oldData: ApiData<T>): ApiData<T> => ({
  state: ApiState.Reloading,
  data: oldData.data,
});

export const makeApiLimitReached = (message: string): ApiData<never> => ({
  state: ApiState.Error,
  errorReason: { message, type: ApiErrorReasonType.ApiLimitReached },
});

export const makeApiQuoteSaved = <T>(data: T): ApiData<T> => ({
  state: ApiState.QuoteSaved,
  data,
});

export const hasRecoverableBusinessError = (apiData: ApiData<unknown>) =>
  apiData?.errorReason?.type === ApiErrorReasonType.InvalidCoverForSumInsured &&
  !apiData?.errorReason?.blockingError;

export const hasNonRecoverableBusinessErrors = (apiData: ApiData<unknown>) =>
  isBusinessError(apiData) &&
  isBlockingError(apiData) &&
  !isPromoCodeError(apiData) &&
  !isIneligiblePromoCodeError(apiData);

export const isErrorWhileSavingQuote = (apiData: ApiData<unknown>) =>
  isApiError(apiData) || isBusinessError(apiData);
